import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { CaretUpDown, Check } from "@phosphor-icons/react";

/** What the plain `<Select>` printed, kept identical so the two cards read the
 *  same before and after: a canonical label if the fleet row has one, else
 *  year/make/model, else the plate, else the id. */
const vehicleName = (v) =>
  v.label || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() || v.license_plate || v.id;

/**
 * Vehicle picker that can be typed into (INS-016 item 4). 68 cars in a plain
 * dropdown is a scroll, so this filters on the label and the plate as you type.
 *
 * The id stays the cmdk `value` because it is the only guaranteed-unique key,
 * and the search runs on `keywords` ONLY — matching the value too would let a
 * typed "cab" or "fed" hit a hex character of somebody's ObjectId.
 *
 * Keeps what the select had: the `id` for the label association, and a
 * placeholder at full muted-foreground strength rather than the design
 * system's 70%, which only reached 3.48:1 (INS-014).
 */
export function VehiclePicker({ id, vehicles, value, onChange, label, testid, placeholder = "Choose a vehicle" }) {
  const [open, setOpen] = useState(false);
  const selected = vehicles.find((v) => v.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          className="w-full justify-between font-normal bg-input border-border hover:bg-input"
          data-testid={testid}
        >
          <span className={selected ? "truncate" : "truncate text-muted-foreground"}>
            {selected ? vehicleName(selected) : placeholder}
          </span>
          <CaretUpDown size={14} className="ml-2 shrink-0 opacity-60" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(_value, search, keywords = []) =>
            keywords.join(" ").toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder="Search make, model or plate…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty data-testid={`${testid}-empty`}>No vehicle matches that.</CommandEmpty>
            <CommandGroup>
              {vehicles.map((v) => (
                <CommandItem
                  key={v.id}
                  value={v.id}
                  keywords={[vehicleName(v), v.license_plate || ""]}
                  onSelect={() => { onChange(v.id); setOpen(false); }}
                  data-testid={`${testid}-option-${v.id}`}
                >
                  <Check size={14} className={`shrink-0 ${v.id === value ? "opacity-100" : "opacity-0"}`} aria-hidden="true" />
                  <span className="truncate">{vehicleName(v)}</span>
                  {v.license_plate && (
                    <span className="ml-auto pl-3 text-xs text-muted-foreground tnum">{v.license_plate}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
